import { apiClient } from "./api-client";
import {
  User,
  Product,
  Address,
  Subscription,
  UserSubscription,
  Wishlist,
  Rating,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  CreateAddressRequest,
  CreateProductRequest,
  UpdateProductRequest,
  PaginationParams,
} from "@/types";

/**
 * Authentication APIs
 */
export const authAPI = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    // Backend expects only username and password as query parameters
    const params = new URLSearchParams({
      username: data.username,
      password: data.password,
    });
    const url = `/users/register?${params.toString()}`;
    console.log("Sending register POST to:", url);
    const response = await apiClient.post<AuthResponse>(url, {});
    console.log("Register API response:", response);
    // Backend returns user object, need to extract what we need
    return {
      access_token: response.access_token || "",
      token_type: response.token_type || "bearer",
      user: response.user || response,
    };
  },

  login: async (data: LoginRequest): Promise<any> => {
    // Backend expects query parameters for login
    const params = new URLSearchParams({
      username: data.username,
      password: data.password,
    });
    const url = `/users/login?${params.toString()}`;
    console.log("Sending login POST to:", url);
    const response = await apiClient.post<any>(url, {});
    console.log("Login API response:", response);
    // Backend returns {"msg": "logged in"} with token in cookie
    return response;
  },

  getCurrentUser: async (): Promise<User> => {
    return apiClient.get<User>("/users/me");
  },
};

/**
 * User APIs
 */
export const userAPI = {
  getUserProfile: async (id: number): Promise<User> => {
    return apiClient.get<User>(`/users/${id}`);
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    return apiClient.put<User>("/users/me", data);
  },

  deleteProfile: async (): Promise<void> => {
    return apiClient.delete<void>("/users/me");
  },
};

/**
 * Product APIs
 */
export const productAPI = {
  getProducts: async (params?: PaginationParams): Promise<Product[]> => {
    return apiClient.get<Product[]>("/products", { params });
  },

  getProduct: async (id: number): Promise<Product> => {
    return apiClient.get<Product>(`/products/${id}`);
  },

  createProduct: async (data: CreateProductRequest): Promise<Product> => {
    return apiClient.post<Product>("/products", data);
  },

  updateProduct: async (
    id: number,
    data: UpdateProductRequest,
  ): Promise<Product> => {
    return apiClient.put<Product>(`/products/${id}`, data);
  },

  deleteProduct: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/${id}`);
  },

  addColor: async (productId: number, colorId: number): Promise<void> => {
    return apiClient.post<void>(`/products/${productId}/colors/${colorId}`, {});
  },

  removeColor: async (productId: number, colorId: number): Promise<void> => {
    return apiClient.delete<void>(`/products/${productId}/colors/${colorId}`);
  },

  addSize: async (productId: number, sizeId: number): Promise<void> => {
    return apiClient.post<void>(`/products/${productId}/sizes/${sizeId}`, {});
  },

  removeSize: async (productId: number, sizeId: number): Promise<void> => {
    return apiClient.delete<void>(`/products/${productId}/sizes/${sizeId}`);
  },

  addMedia: async (
    productId: number,
    data: FormData,
  ): Promise<Record<string, any>> => {
    return apiClient.post<Record<string, any>>(
      `/products/${productId}/media`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },

  getMedia: async (productId: number): Promise<Record<string, any>[]> => {
    return apiClient.get<Record<string, any>[]>(`/products/${productId}/media`);
  },

  deleteMedia: async (mediaId: number): Promise<void> => {
    return apiClient.delete<void>(`/products/media/${mediaId}`);
  },
};

/**
 * Rating APIs
 */
export const ratingAPI = {
  rateProduct: async (
    productId: number,
    data: { score: number; comment?: string },
  ): Promise<Rating> => {
    return apiClient.post<Rating>(`/products/${productId}/ratings`, data);
  },

  getProductRatings: async (productId: number): Promise<Rating[]> => {
    return apiClient.get<Rating[]>(`/products/${productId}/ratings`);
  },

  updateRating: async (
    ratingId: number,
    data: { score?: number; comment?: string },
  ): Promise<Rating> => {
    return apiClient.put<Rating>(`/products/ratings/${ratingId}`, data);
  },

  deleteRating: async (ratingId: number): Promise<void> => {
    return apiClient.delete<void>(`/products/ratings/${ratingId}`);
  },
};

/**
 * Wishlist APIs
 */
export const wishlistAPI = {
  addToWishlist: async (productId: number): Promise<Wishlist> => {
    return apiClient.post<Wishlist>(`/products/wishlist/${productId}`, {});
  },

  getWishlist: async (): Promise<Wishlist[]> => {
    return apiClient.get<Wishlist[]>("/products/wishlist");
  },

  removeFromWishlist: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/wishlist/${id}`);
  },
};

/**
 * Address APIs
 */
export const addressAPI = {
  createAddress: async (data: CreateAddressRequest): Promise<Address> => {
    return apiClient.post<Address>("/users/addresses", data);
  },

  getAddresses: async (): Promise<Address[]> => {
    return apiClient.get<Address[]>("/users/addresses");
  },

  updateAddress: async (
    id: number,
    data: Partial<CreateAddressRequest>,
  ): Promise<Address> => {
    return apiClient.put<Address>(`/users/addresses/${id}`, data);
  },

  deleteAddress: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/users/addresses/${id}`);
  },
};

/**
 * Subscription APIs
 */
export const subscriptionAPI = {
  getSubscriptions: async (): Promise<Subscription[]> => {
    return apiClient.get<Subscription[]>("/users/subscriptions");
  },

  getSubscription: async (id: number): Promise<Subscription> => {
    return apiClient.get<Subscription>(`/users/subscriptions/${id}`);
  },

  subscribe: async (id: number): Promise<UserSubscription> => {
    return apiClient.post<UserSubscription>(
      `/users/subscriptions/${id}/subscribe`,
      {},
    );
  },

  getActiveSubscription: async (): Promise<UserSubscription> => {
    return apiClient.get<UserSubscription>("/users/subscriptions/active");
  },

  getProductLimit: async (): Promise<{ limit: number }> => {
    return apiClient.get<{ limit: number }>("/users/products/limit");
  },
};

/**
 * Color APIs
 */
export const colorAPI = {
  createColor: async (data: {
    name: string;
    hex_code: string;
  }): Promise<Color> => {
    return apiClient.post<Color>("/products/colors", data);
  },

  getColors: async (): Promise<Color[]> => {
    return apiClient.get<Color[]>("/products/colors");
  },

  deleteColor: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/colors/${id}`);
  },
};

/**
 * Size APIs
 */
export const sizeAPI = {
  createSize: async (data: { name: string }): Promise<Size> => {
    return apiClient.post<Size>("/products/sizes", data);
  },

  getSizes: async (): Promise<Size[]> => {
    return apiClient.get<Size[]>("/products/sizes");
  },

  deleteSize: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/sizes/${id}`);
  },
};

// Type exports for convenience
type Color = Record<string, any>;
type Size = Record<string, any>;
